import { useEffect, useRef, useState } from "react";
import Button from "@/components/base/Button";
import { useSiteData } from "@/context/SiteDataContext";
import { useT } from "@/lib/useSiteText";
import type { HeroSlideItem } from "@/types/content";

const AUTOPLAY_MS = 6000;
const SWIPE_THRESHOLD_PX = 50;

export default function Hero() {
  const { data, whatsappLink } = useSiteData();
  const t = useT();
  const page = data.pages.home;
  const ubicacion = t("home.hero.ubicacion");

  const configured = (data.contentBlocks.home?.hero_slides ?? []).filter((s) => s.imagen_url);
  const slides: HeroSlideItem[] =
    configured.length > 0
      ? configured
      : [
          {
            id: "default",
            imagen_url: page.hero_imagen_url,
            imagen_alt: page.hero_imagen_alt,
            titulo: page.hero_titulo,
            descripcion: page.hero_descripcion,
          },
        ];

  const multiple = slides.length > 1;
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const touchStartX = useRef<number | null>(null);
  const current = Math.min(active, slides.length - 1);

  const goTo = (index: number) => setActive((index + slides.length) % slides.length);

  useEffect(() => {
    if (!multiple || paused || reducedMotion) return;
    const timer = window.setTimeout(() => setActive((i) => (i + 1) % slides.length), AUTOPLAY_MS);
    return () => window.clearTimeout(timer);
  }, [current, multiple, paused, reducedMotion, slides.length]);

  function handleTouchEnd(clientX: number) {
    if (touchStartX.current === null) return;
    const dx = clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) >= SWIPE_THRESHOLD_PX) goTo(current + (dx < 0 ? 1 : -1));
  }

  return (
    <section
      className="relative min-h-screen flex items-center"
      aria-roledescription={multiple ? "carousel" : undefined}
      aria-label={multiple ? "Banner principal" : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => handleTouchEnd(e.changedTouches[0].clientX)}
    >
      {slides.map((slide, i) => (
        <img
          key={slide.id}
          src={slide.imagen_url}
          alt={i === current ? slide.imagen_alt : ""}
          aria-hidden={i !== current}
          className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-1000 ${
            i === current ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />

      <div className="relative w-full mx-auto max-w-7xl px-4 md:px-6 pt-32 pb-24 text-center">
        <div className="animate-fade-up">
          {ubicacion && (
            <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 backdrop-blur px-4 py-1.5 text-xs md:text-sm font-medium text-background-50">
              <i className="ri-map-pin-2-line" />
              {ubicacion}
            </span>
          )}

          <div className="grid">
            {slides.map((slide, i) => {
              const Title = i === current ? "h1" : "h2";
              return (
                <div
                  key={slide.id}
                  aria-hidden={i !== current}
                  className={`col-start-1 row-start-1 transition-all duration-700 ${
                    i === current ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
                  }`}
                >
                  <Title className="mt-6 font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-background-50 leading-tight max-w-4xl mx-auto">
                    {slide.titulo}
                  </Title>
                  <p className="mt-5 text-base md:text-xl text-background-200 max-w-2xl mx-auto leading-relaxed">
                    {slide.descripcion}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button to="/bodegas" icon="ri-store-2-line" className="w-full sm:w-auto px-8 py-3.5">
              {t("home.hero.boton_bodegas")}
            </Button>
            <Button
              href={whatsappLink()}
              variant="accent"
              icon="ri-whatsapp-line"
              className="w-full sm:w-auto px-8 py-3.5"
            >
              {t("home.hero.boton_whatsapp")}
            </Button>
          </div>
        </div>
      </div>

      {multiple && (
        <>
          <button
            type="button"
            aria-label="Imagen anterior"
            onClick={() => goTo(current - 1)}
            className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-background-50/15 backdrop-blur text-background-50 hover:bg-background-50/30 transition items-center justify-center cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-2xl" />
          </button>
          <button
            type="button"
            aria-label="Imagen siguiente"
            onClick={() => goTo(current + 1)}
            className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-background-50/15 backdrop-blur text-background-50 hover:bg-background-50/30 transition items-center justify-center cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-2xl" />
          </button>
          <div className="absolute bottom-28 md:bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                aria-label={`Ir a la imagen ${i + 1}`}
                aria-current={i === current}
                onClick={() => goTo(i)}
                className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                  i === current ? "w-8 bg-background-50" : "w-2.5 bg-background-50/50 hover:bg-background-50/80"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
